import { useFormik } from 'formik'
import { useSnackbar } from 'notistack'

import { ConsoleDialog } from '@/components/ConsoleDialog'
import { FormikTextField } from '@/components/inputs/FormikTextField'
import { MultiSelectBox } from '@/components/inputs/MultiSelectBox'
import { OperatorAutocomplete } from '@/components/inputs/OperatorAutocomplete'
import { SelectBox } from '@/components/inputs/SelectBox'
import {
  DistributionListRecipientType,
  DistributionListSportKey,
  distributionListSportLabelsMap,
  possibleDistributionListSports,
  possibleRecipientTypes
} from '@/constants/distributionList'
import { ApiError } from '@/service/ApiError'
import {
  addDistributionListRecipient,
  addDistributionListRecipientSport,
  deleteDistributionListRecipientSport
} from '@/service/distributionListService'
import { DistributionRecipientWithOperatorName } from '@/stores/distributionRecipients'
import { FormDialogProps } from '@/types/forms'
import { formikSaveButtonProps } from '@/utils/forms'
import { getFormError, onFieldChange } from '@/utils/forms/formik'

import { distributionRecipientFormValidationSchema } from './distributionRecipient.validation'

type Props = FormDialogProps<DistributionRecipientWithOperatorName>

type FormValues = {
  emailAddress: string
  recipientType: DistributionListRecipientType
  operatorId?: string | null
  sports: DistributionListSportKey[]
}

export const DistributionRecipientForm = ({ open, defaultValues, onClose, onSubmit }: Props) => {
  const { enqueueSnackbar } = useSnackbar()

  const isEditMode = !!defaultValues?.emailAddress
  const title = isEditMode ? 'Edit Recipient' : 'Add Recipient'

  const formik = useFormik<FormValues>({
    initialValues: {
      emailAddress: defaultValues?.emailAddress || '',
      recipientType: (defaultValues?.recipientType as FormValues['recipientType']) || 'OPERATOR',
      operatorId: defaultValues?.operatorId?.toString() || null,
      sports: defaultValues?.sports?.length ? defaultValues?.sports : []
    },

    validationSchema: distributionRecipientFormValidationSchema,

    onSubmit: async (values, { setSubmitting }) => {
      if (!values.emailAddress || !values.recipientType) return

      try {
        setSubmitting(true)

        if (isEditMode) {
          // Backend does not proide bulk update for sports, so we need to handle it manually,
          // delete every single sport that is not in the new list and add every single sport
          // that is not in the old list each in its own request

          // We separate the add and delete operations to prevent potential race conditions —
          // ensuring all new sports are fully added for each recipient before any old sports are removed.
          // This avoids situations where a delete request could run before an add completes for the same recipient.
          const sportsToAdd = values.sports.filter((sport) => !defaultValues.sports.includes(sport))
          const sportsToDelete = defaultValues.sports.filter(
            (sport) => !values.sports.includes(sport)
          )

          if (sportsToAdd.length > 0) {
            await Promise.all(
              sportsToAdd.map((sport) =>
                addDistributionListRecipientSport({
                  emailAddress: values.emailAddress,
                  sport
                })
              )
            )
          }

          if (sportsToDelete.length > 0) {
            await Promise.all(
              sportsToDelete.map((sport) =>
                deleteDistributionListRecipientSport({
                  emailAddress: values.emailAddress,
                  sport
                })
              )
            )
          }
        } else {
          await addDistributionListRecipient({
            emailAddress: values.emailAddress,
            recipientType: values.recipientType,
            operatorId: values.operatorId ? values.operatorId : null,
            sports: values.sports
          })
        }

        onSubmit?.()

        if (isEditMode) {
          enqueueSnackbar(`Updated sports for recipient ${values.emailAddress}`, {
            variant: 'success'
          })
        } else {
          enqueueSnackbar(`Recipient ${values.emailAddress} has been created`, {
            variant: 'success'
          })
        }

        setSubmitting(false)
        handleClose()
      } catch (e) {
        const errorMessage = [
          `Failed to ${isEditMode ? 'update' : 'create'} recipient`,
          (e as unknown as ApiError)?.message
        ]
          .filter(Boolean)
          .join(': ')
        enqueueSnackbar(errorMessage, { variant: 'error' })
        setSubmitting(false)
        // Throw the error again to be caught by dialog and doesn't close it
        throw errorMessage
      }
    }
  })

  const handleClose = () => {
    formik.resetForm()
    onClose?.()
  }

  return (
    <ConsoleDialog
      open={open}
      SaveButtonProps={formikSaveButtonProps(formik)}
      title={title}
      onClose={handleClose}
    >
      <FormikTextField
        label="E-mail"
        required
        name="emailAddress"
        formik={formik}
        disabled={isEditMode}
      />

      <SelectBox
        label="Recipient Type"
        name="recipientType"
        options={possibleRecipientTypes}
        required
        fullWidth
        disabled={isEditMode}
        value={formik.values.recipientType}
        onChange={(value) => {
          onFieldChange(formik, 'recipientType', value)
          if (value === 'ADMIN') {
            onFieldChange(formik, 'sports', [])
            onFieldChange(formik, 'operatorId', null)
          } else {
            onFieldChange(formik, 'operatorId', undefined)
          }
        }}
        error={getFormError(formik, 'recipientType')}
      />

      <OperatorAutocomplete
        defaultValue={formik.values.operatorId?.toString() || null}
        onChange={(value) => onFieldChange(formik, 'operatorId', value)}
        required={formik.values.recipientType === 'OPERATOR'}
        disabled={isEditMode || formik.values.recipientType === 'ADMIN'}
        error={getFormError(formik, 'operatorId')}
        displayOperatorId
      />

      <MultiSelectBox
        label="Sports"
        options={possibleDistributionListSports.sort((a, b) => a.localeCompare(b))}
        formatListOption={(option) => distributionListSportLabelsMap[option]}
        formatChipOption={(option) => distributionListSportLabelsMap[option]}
        name="sports"
        value={formik.values.sports}
        onChange={(value) => onFieldChange(formik, 'sports', value)}
        error={getFormError(formik, 'sports')}
        required={formik.values.recipientType !== 'ADMIN'}
        fullWidth
        excludeSelected
        noOptionsMessage="No sports available"
        disabled={formik.values.recipientType === 'ADMIN'}
        startAdornment={formik.values.recipientType === 'ADMIN' ? 'All' : undefined}
      />
    </ConsoleDialog>
  )
}
